import React from 'react';
import { DevicePropertiesDetails, DeviceChannelData, SubscriptionStatus } from '../../types/device';
import { Data, isDataLoaded, isDataError } from '../../types/data-providers';
import { AppRoute } from '../../routes';
import { DeviceInformation } from '../../components/DeviceInformation';
import { TemperatureGraphDataProvider } from '../../data/DataProviders/TemperatureGraphDataProvider';
import { TemperatureGraph } from '../../components/TemperatureGraph/TemperatureGraph';
import { HumidityGraphDataProvider } from '../../data/DataProviders/HumidityGraphDataProvider';
import { HumidityGraph } from '../../components/Humidity/HumidityGraph';
import { LoadGraphDataProvider } from '../../data/DataProviders/LoadGraphDataProvider';
import { LoadGraph } from '../../components/LoadGraphs/LoadGraph';
import { SensorOverView } from '../../components/SensorTrend/SensorOverview';
import { SensorListDataProvider } from '../../data/DataProviders/SensorListDataProvider';
import { PhaseResolvedGraphOverview } from '../../components/PhaseResolvedGraphOverview/PhaseResolvedGraphOverview';
import { DatetimeListDataProvider } from '../../data/DataProviders/DatetimeListDataProvider';
import { RelativeConditionIndex } from '../../components/RelativeConditionIndex/RelativeConditionIndex';
import { RelativeConditionIndexProvider } from '../../data/DataProviders/RelativeConditionIndexProvider';
import { List, Card, Typography, Link } from '@mui/material';

import { ReusableTrendsCardHeader } from '../../components/ReusableTrendsGraph';
import { TrendsCardHeaderNotLoaded } from '../../components/ReusableTrendsGraph/ReusableTrendCardContentLoader';
import { CenteredContent } from '../../components/CenteredContent';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { DeviceTrendsCard } from '../../components/DeviceTrends/DeviceTrends';
import { DeviceMasterProvider } from '../../data/DataProviders/DeviceMasterProvider';
import { DeviceTrendDataProvider } from '../../data/DataProviders/DeviceTrendDataProvider';
import { TrendChannelsProvider } from '../../data/DataProviders/TrendChannelsProvider';
import { ContentContainer } from '../../components/ContentContainer';
import { ReportGeneratePdf } from '../../components/Reports/ReportGeneration';
import { DeviceReportDataProvider } from '../../data/DataProviders/DeviceReportTableDataProvider';
import { DeviceProperties } from '../../components/DeviceProperties';
import { useGetSiteSubscriptionDetails } from '../../data/DataProviders/GetSiteSubscriptionProvider';

import { useGetOrganizationCheckDetails } from '../../data/DataProviders/GetOrgCheckProvider';

import { hideTrends } from '../../lib/hide-trends-helper';
import { EmptyState } from '@brightlayer-ui/react-components';
import { CONTACT_EMAIL } from '../../constants';
import { ShowChart } from '@mui/icons-material';
import { DataIndicators } from '../../components/DataIndicators/DataIndicators';
type DeviceData = {
    deviceProperties: Data<DevicePropertiesDetails>;
    channelData: Data<DeviceChannelData[]>;
};

export const Overview: React.FC<{
    deviceData: DeviceData;
    tabIndex: number;
    appRoute: AppRoute;
    deviceId: string;
    testId?: string;
}> = (props): JSX.Element => {
    const [tabValue, setTabValue] = React.useState(props.tabIndex);

    const meshSiteSubscriptionDetails = useGetSiteSubscriptionDetails();
    const getOrgCheckDetails = useGetOrganizationCheckDetails();
    const [getOrgTypeData, setgetOrgTypeData] = React.useState<any | null>(null);
    const [showError, setShowError] = React.useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = React.useState(false);
    const [trendsEnabled, setTrendsEnabled] = React.useState<boolean>(false);
    const { t } = useLanguageLocale();
    const [showTrends, setShowTrends] = React.useState(false);
    React.useEffect(() => {
        setTabValue(props.tabIndex);
    }, [props.tabIndex]);

    React.useEffect(() => {
        if (isDataLoaded(getOrgCheckDetails)) {
            setgetOrgTypeData(getOrgCheckDetails.value);
        }
    }, [getOrgCheckDetails]);
    React.useEffect(() => {
        if (meshSiteSubscriptionDetails !== null) {
            if (isDataLoaded(meshSiteSubscriptionDetails)) {
                setTrendsEnabled(meshSiteSubscriptionDetails.value.cloudDataSync.isTrendsEnabled);
                setShowTrends(true);
                const error = hideTrends(meshSiteSubscriptionDetails.value);
                setShowError(error);
                if (meshSiteSubscriptionDetails.value.subscriptionDetails.subscriptionStatus === SubscriptionStatus.Expired) {
                    setSubscriptionStatus(false)
                } else {
                    setSubscriptionStatus(true)
                }


            }
            if (isDataError(meshSiteSubscriptionDetails)) {
                setShowTrends(false);
            }
        }
    }, [meshSiteSubscriptionDetails]);

    return (
        <>
            {tabValue === 0 && (
                <ContentContainer testId={props.testId} paddingTop="0px">
                    {getOrgTypeData === null || subscriptionStatus ? (
                        <DeviceInformation deviceData={props.deviceData} appRoute={props.appRoute} />
                    ) : (
                        <CenteredContent style={{ height: '30vh' }}>
                            <DataIndicators
                                emptyIcon="Devices"
                                emptyMessage={t('DASHBOARD.NO_DEVICE_DETAILS')}
                                errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                                data={props.deviceData.deviceProperties}
                                isEmpty={true}
                            />
                        </CenteredContent>
                    )}
                </ContentContainer>
            )}

            {tabValue === 1 &&
                isDataLoaded(props.deviceData.deviceProperties) &&
                // (getOrgTypeData ? (
                //     trendsEnabled && !showError ? (
                ((getOrgTypeData && trendsEnabled && !showError && showTrends) || getOrgTypeData === null ? (
                    <TrendChannelsProvider deviceId={props.deviceId}>
                        <DeviceTrendDataProvider
                            deviceId={props.deviceId}
                            deviceName={props.deviceData.deviceProperties.value.name}
                        >
                            <DeviceMasterProvider>
                                <ContentContainer
                                    testId="device-details-trends-page"
                                    justifyContent="center"
                                    paddingTop="0px"
                                    minHeight="70vh"
                                >
                                    <DeviceTrendsCard
                                        deviceId={props.deviceId}
                                        deviceName={props.deviceData.deviceProperties.value.name}
                                        large
                                    />
                                </ContentContainer>
                            </DeviceMasterProvider>
                        </DeviceTrendDataProvider>
                    </TrendChannelsProvider>
                ) : (
                    <>
                        <ContentContainer
                            testId="device-details-trends-page"
                            justifyContent="center"
                            paddingTop="0px"
                            minHeight="70vh"
                        >
                            <Card style={{ margin: '15px', marginTop: 15, marginBottom: 25, flex: 1 }}>
                                <List
                                    disablePadding
                                    subheader={
                                        <ReusableTrendsCardHeader
                                            title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                        // large={props.large}
                                        >
                                            <TrendsCardHeaderNotLoaded />
                                        </ReusableTrendsCardHeader>
                                    }
                                >
                                    <CenteredContent style={{ height: '30vh' }}>
                                        <EmptyState
                                            icon={<ShowChart id="fail-icon" style={{ fontSize: '24px' }} />}
                                            title={'No Trends'}
                                            actions={
                                                <>
                                                    <Typography
                                                        variant="subtitle2"
                                                        // color="primary"
                                                        style={{ textAlign: 'center' }}
                                                    >
                                                        {'You don’t have access to trends.'}
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle2"
                                                        // color="primary"
                                                        style={{ textAlign: 'center' }}
                                                    >
                                                        {'Contact Eaton support at'}{' '}
                                                        <Link underline="always" href={`mailto:${CONTACT_EMAIL}`}>
                                                            {CONTACT_EMAIL}
                                                        </Link>{' '}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </CenteredContent>
                                    {/* <DataEmptyIndicator
                                icon={'Devices'}
                                // icon={props.value.icon}
                                data={{ state: DataState.Loaded, value: {} }}
                                message={t('DASHBOARD.ERROR_LOADING_TRENDS')}
                                inline
                                isEmpty
                            /> */}
                                </List>
                            </Card>
                        </ContentContainer>
                    </>
                ))}

            {tabValue === 2 && (
                <ContentContainer testId={props.testId} paddingTop="0px">
                    <DeviceProperties deviceData={props.deviceData} appRoute={props.appRoute} />
                </ContentContainer>
            )}

            {tabValue === 3 && (
                <>
                    <ContentContainer testId={props.testId}>
                        <div style={{ marginBottom: '30px' }}>
                            <RelativeConditionIndexProvider deviceId={props.deviceId}>
                                <RelativeConditionIndex />
                            </RelativeConditionIndexProvider>
                        </div>
                        <div style={{ marginBottom: '30px' }}>
                            <TemperatureGraphDataProvider deviceId={props.deviceId}>
                                <TemperatureGraph />
                            </TemperatureGraphDataProvider>
                        </div>
                        <div style={{ marginBottom: '30px' }}>
                            <HumidityGraphDataProvider deviceId={props.deviceId}>
                                <HumidityGraph />
                            </HumidityGraphDataProvider>
                        </div>
                        <div style={{ marginBottom: '30px' }}>
                            <LoadGraphDataProvider deviceId={props.deviceId}>
                                <LoadGraph />
                            </LoadGraphDataProvider>
                        </div>
                    </ContentContainer>
                </>
            )}
            {tabValue === 4 && (
                <ContentContainer testId={props.testId}>
                    <SensorListDataProvider deviceId={props.deviceId}>
                        <SensorOverView deviceId={props.deviceId} />
                    </SensorListDataProvider>
                </ContentContainer>
            )}

            {tabValue === 5 && (
                <ContentContainer testId={props.testId}>
                    <DatetimeListDataProvider deviceId={props.deviceId}>
                        <PhaseResolvedGraphOverview deviceId={props.deviceId}></PhaseResolvedGraphOverview>
                    </DatetimeListDataProvider>
                </ContentContainer>
            )}

            {tabValue === 6 && (
                <ContentContainer testId={props.testId}>
                    <DeviceReportDataProvider deviceId={props.deviceId}>
                        <ReportGeneratePdf />
                    </DeviceReportDataProvider>
                </ContentContainer>
            )}
        </>
    );
};
